import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Email, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { userAPI } from '../../../services/api';

const EmailReset = () => {
  const [step, setStep] = useState(1); // 1: enter old email, 2: enter new email
  const [oldEmail, setOldEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const handleVerifyOldEmail = async (e) => {
    e.preventDefault();
    if (!oldEmail.trim()) {
      setVerifyError('Please enter the old email address');
      return;
    }
    setVerifyError('');
    setVerifyLoading(true);
    try {
      // We'll just check by attempting a lookup — but to avoid exposing a separate endpoint,
      // we rely on the reset-email endpoint to validate. So just advance the step.
      setVerified(true);
      setStep(2);
    } catch (error) {
      setVerifyError('Something went wrong. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('Please enter the new email address');
      return;
    }
    if (oldEmail.toLowerCase() === newEmail.toLowerCase()) {
      toast.error('New email must be different from old email');
      return;
    }
    setLoading(true);
    try {
      await userAPI.resetEmail(oldEmail, newEmail);
      toast.success('Email updated! Welcome credentials sent to the new email address.');
      // Reset form
      setOldEmail('');
      setNewEmail('');
      setStep(1);
      setVerified(false);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update email';
      toast.error(msg);
      if (msg.toLowerCase().includes('no user found')) {
        // Go back to step 1 so they can re-enter the old email
        setStep(1);
        setVerified(false);
        setVerifyError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Email Reset
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Update a user's email address and resend their welcome credentials to the new email.
      </Typography>

      <Paper sx={{ maxWidth: 520, p: 4 }}>
        {/* Step 1: Old Email */}
        <Box component="form" onSubmit={handleVerifyOldEmail}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Step 1 — Enter existing email
          </Typography>
          <TextField
            fullWidth
            label="Old Email Address"
            type="email"
            value={oldEmail}
            onChange={(e) => {
              setOldEmail(e.target.value);
              setVerifyError('');
              if (step === 2) {
                setStep(1);
                setVerified(false);
                setNewEmail('');
              }
            }}
            disabled={loading}
            error={!!verifyError}
            helperText={verifyError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: verified && step === 2 ? (
                <InputAdornment position="end">
                  <CheckCircle color="success" fontSize="small" />
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 2 }}
          />
          {step === 1 && (
            <Button
              type="submit"
              variant="contained"
              disabled={verifyLoading || !oldEmail.trim()}
              fullWidth
            >
              {verifyLoading ? <CircularProgress size={22} /> : 'Continue'}
            </Button>
          )}
        </Box>

        {/* Step 2: New Email */}
        {step === 2 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Step 2 — Enter new email
              </Typography>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.82rem' }}>
                A new password will be auto-generated and sent along with login credentials to the new email address.
              </Alert>
              <TextField
                fullWidth
                label="New Email Address"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !newEmail.trim()}
                fullWidth
                size="large"
              >
                {loading ? <CircularProgress size={22} /> : 'Update Email & Send Credentials'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmailReset;
